import React, { useState, useEffect } from 'react';
import { ArrowLeft, SquarePlus, Info, Pencil, Trash2 } from 'lucide-react';
import styles from './QuestionsIndeTab.module.scss';
import { DEBUG, PATH } from '../../../../utils/constants';

import RichTextEditor from '../../../../utils/RichTextEditor/RichTextEditor';
import SmartRenderer from '../../../../utils/SmartRenderer/SmartRenderer';
import ImageEditor from '../../../../utils/ImageEditor/ImageEditor';

// import ReponsesTab from '../ReponsesTab/ReponsesTab';
import Flag from '../../../../utils/Flags/Flags';


const path = PATH;
const debug = DEBUG;


function QuestionsIndeTab({ gameInfos, questions = [], onTabChange, onCreateQuestion, onUpdateQuestion, onUpdateQuestionImage, onUpdateQuestionOptions, onDeleteQuestion, onQuestionInfosSelect }) {
   
  const [questionData, setQuestionData] = useState({ libelle: '', type: 'libre' });

  // Modale d'ajout de question
  const [modalOpen, setModalOpen] = useState(false);

  // Modale d'édition d'une question
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Pilote la modale d'édition de question
  const [activeEditTab, setActiveEditTab] = useState('A'); // Onglet par défaut de la fenêtre d'édition

  // Modale d'édition de l'image principale
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);

  // States utilitaires pour gérer champs requis (1. ajout / 2. édition d'une question)
  const [questionText, setQuestionText] = useState(''); // 1. texte brut (sans formattage)
  const [createQuestionError, setCreateQuestionError] = useState('');
  const [editLibelleText, setEditLibelleText] = useState(''); // 2. texte brut (sans formattage)
  const [editQuestionError, setEditQuestionError] = useState('');

  // Panneau d'édition du contenu
  const [currentItem, setCurrentItem] = useState(null);
  const [editLibelle, setEditLibelle] = useState(''); // pour l'édition = nouveau libelle
  const [editType, setEditType] = useState(''); // pour l'édition = nouveau type de question
  const [editHelper, setEditHelper] = useState(''); // pour l'édition = nouvel indice textuel
  const [localTempImageHelper, setLocalTempImageHelper] = useState(null); // indice - image locale temporaire  
  const [shouldDeleteHelperImage, setShouldDeleteHelperImage] = useState(null);
  
  // Panneau d'édition des options
  const [editKey, setEditKey] = useState(''); // pour l'édition = nouvel indice pour mot de passe
  const [editMessage, setEditMessage] = useState(''); // pour l'édition = nouveau message de progression...
  const [localTempImageKey, setLocalTempImageKey] = useState(null); // indice - image locale temporaire  
  const [shouldDeleteKeyImage, setShouldDeleteKeyImage] = useState(null);

  // Panneau d'édition de l'image
  const [tempMainImage, setTempMainImage] = useState(null); // indice - image locale temporaire  
  const [shouldDeleteMainImage, setShouldDeleteMainImage] = useState(null); // indice - image locale temporaire  

  // Modale de suppression de question
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);


  useEffect(() => {
      if (debug) console.log("Questions reçues :", questions);
  }, [questions]);


// --------------------- EVENTS --------------------------

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => {
    setModalOpen(false);
    setQuestionData({ libelle: '', type: 'libre' });
    // Mettre à jour/gérer les previews...
  };
    
  const handleChange = (e) => {
      const { name, value } = e.target;
      setQuestionData({ ...questionData, [name]: value });
  };

  const handleSubmit = (e) => {
      e.preventDefault();
      if (!questionText.trim()) {
          setCreateQuestionError('Le libellé est requis.');
          return;
      }
      onCreateQuestion(gameInfos.id, questionData);
      handleCloseModal();
  };

  const openEditModal = (question, e) => {
      e.stopPropagation();
      setCurrentItem(question);
      setEditLibelle(question.libelle);
      setEditType(question.type || 'libre');
      setEditHelper(question.helper || '');

      setEditKey(question.cle_indice || '');
      setEditMessage(question.message || '');
      
      setLocalTempImageHelper(null);
      setLocalTempImageKey(null);
      setShouldDeleteHelperImage(false);
      setShouldDeleteKeyImage(false);

      setActiveEditTab('A');
      setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {

      e.preventDefault();

      const payload = {
          libelle: editLibelle,
          type: editType,
          helper: editHelper,
      };

      if (localTempImageHelper instanceof File) {
          payload.img_helper = localTempImageHelper;
      }

      if (shouldDeleteHelperImage) {
          payload.delete_img_helper = true;
      }

      const success = await onUpdateQuestion(currentItem.id, payload);

      if (success) {
          setIsEditModalOpen(false);
          setLocalTempImageHelper(null);
      }
  };

  const handleOptionsSubmit = async (e) => {

      e.preventDefault();

      const payload = {
          cle_indice: editKey, // à vérifier côté back
          message: editMessage,
      };

      if (localTempImageKey instanceof File) {
          payload.img_indice = localTempImageKey;
      }

      if (shouldDeleteKeyImage) {
          payload.delete_img_indice = true;
      }

      const success = await onUpdateQuestionOptions(currentItem.id, payload);

      if (success) {
          setIsEditModalOpen(false);
          setLocalTempImageKey(null);
      }
  };

  const resetEditModal = () => {
      setEditLibelle('');
      setEditType('libre');
      setEditHelper('');
      setLocalTempImageHelper(null);
      setEditKey('');
      setEditMessage('');
      setLocalTempImageKey(null);
      setShouldDeleteHelperImage(null);
      setShouldDeleteKeyImage(null);
      setCurrentItem(null);
      setActiveEditTab('A');
  };

  const openImageEditModal = (question) => {
      setCurrentItem(question);
      setTempMainImage(null);
      setIsEditImageModalOpen(true);
  };

  const openDeleteModal = (question, e) => {
      e.stopPropagation();
      setCurrentItem(question);
      setIsDeleteModalOpen(true);
  };

  const handleGoBack = () => {
      onTabChange('games');
  };
      
// --------------------- RENDERINGS --------------------------

  return (
    <div className={styles.parcoursTab}>

      {/* Bouton de retour */}
      <div className={styles.backButtonContainer}>
        <button className={styles.backButton} onClick={handleGoBack}> <ArrowLeft />Retour à la liste des parcours</button>
      </div>

      {/* ====== Bouton d'ajout de question ====== */}
      <button className={styles.addQuestionButton} onClick={handleOpenModal}> <SquarePlus />Ajouter une question </button>

      <hr className={styles.hr} />

      {/* ====== Liste des questions ====== */}
      <p>Liste des questions</p>
      {questions.length > 0 ? (
        <ul className={styles.questionList}>
          {[...questions]
            .sort((a, b) => (a.placement ?? 0) - (b.placement ?? 0)) // Gestion des valeurs nulles
            .map((question, index) => (
              <div key={question.id} className={styles.listRow}>
                <li className={styles.questionItem}>
                  <div className={styles.questionInfo} onClick={(e) => {
                    e.stopPropagation();
                    onQuestionInfosSelect(question);
                  }}>
                    <div><strong>{index + 1} </strong></div> 
                    <div className={styles.typeInfo}>
                      <div className="rt-content"><SmartRenderer content={question.libelle} /></div>
                      - <em>Type: {question.type}</em>
                      <button className={styles.manageAnswersButton}>Cliquer pour gérer les réponses</button>
                    </div>
                  </div>

                  <div className={styles.preview} onClick={() => openImageEditModal(question)}>
                      {console.log("Image URL:", question.img ? `${PATH}${question.image}` : 'placeholder')}
                      <img
                        src={question.img ? `${PATH}${question.img}` : `/assets/img/placeholder.jpg`}
                        alt={`Illustration du jeu`}
                        className={styles.previewImage}
                      />
                      <button className={styles.manageImgButton}>Modifier l'image</button>
                  </div>

                </li>
                <div className={styles.modButtons}>
                  <button className={styles.editButton} onClick={(e) => openEditModal(question, e)}>
                    <Pencil />
                  </button>
                  <button className={styles.deleteButton} onClick={(e) => openDeleteModal(question, e)}>
                    <Trash2 />
                  </button>
                </div>
              </div>
            ))}
        </ul>
      ) : (
        <p>Aucune question pour ce parcours.</p>
      )}

      {/* ====== Modale d'ajout de question ====== */}
      {modalOpen && (
          <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                  <h3>Créer une question</h3>
                  <form onSubmit={handleSubmit}>

                      <div className={styles.formControl}>
                          <label>Type de question</label>
                          <select name="type" value={questionData.type} onChange={handleChange}>
                              <option value="libre">Libre</option>
                              <option value="qcm">QCM</option>
                          </select>
                      </div>

                      
                      <div className={styles.formControl}>
                          <label>Libellé de la question</label>
                          {/* <textarea name="libelle" value={questionData.libelle} onChange={handleChange} required /> */}
                          <RichTextEditor
                                value={questionData.libelle}
                                toolbar="minimal"
                                placeholder="Intitulé de la question…"
                                onChange={({ markdown, text }) => {
                                    setQuestionData(prev => ({ ...prev, libelle: markdown }));
                                    setQuestionText(text || '');
                                    if (createQuestionError) setCreateQuestionError('');
                                }}
                          />
                      </div>

                      <p className={styles.helpText}>
                          <Info size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          <strong>Astuce :</strong> Une fois la question créée, vous pourrez configurer tous les détails 
                          (image principale, indice, options avancées, réponses...) en cliquant sur le bouton 
                          <Pencil size={16} style={{ verticalAlign: 'middle', margin: '0 6px' }} />
                          <em>Modifier</em> ou sur l’image de la question dans la liste.
                      </p>

                      <div className={styles.buttonContainer}>
                          <button className={styles.cancelButton} type="button" onClick={handleCloseModal}>Annuler</button>
                          <button className={styles.saveButton} type="submit" disabled={!questionText.trim()} aria-disabled={!questionText.trim()}>Créer la question</button>
                      </div>
                      
                  </form>
              </div>
          </div>
      )}

      {/* ====== Modale d'édition de question ====== */}
      {isEditModalOpen && currentItem && (
          <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>

                  {/* Onglets */}
                  <div className={styles.tabSelector}>
                      <button className={activeEditTab === 'A' ? styles.activeTab : ''} onClick={() => setActiveEditTab('A')}>
                          Contenu
                      </button>
                      <button className={activeEditTab === 'B' ? styles.activeTab : ''} onClick={() => setActiveEditTab('B')}>
                          Autres options
                      </button>
                  </div>

                  {activeEditTab === 'A' && (
                      <>
                          <h3>Modifier la question</h3>

                          <hr className={styles.hr} />

                          <form onSubmit={handleEditSubmit} >
                              <div className={styles.formControl}>
                                  <label htmlFor="editLibelle">Libellé</label>
                                  <RichTextEditor
                                      value={editLibelle}               // peut être MD ou HTML, l’éditeur gère
                                      toolbar="minimal"
                                      placeholder="Intitulé de la question…"
                                      onChange={({ markdown }) => setEditLibelle(markdown)}  // on stocke en MD
                                  />
                              </div>

                              <div className={styles.formControl}>
                                  <label htmlFor="editType">Type de question</label>
                                  <select id="editType" value={editType} onChange={(e) => setEditType(e.target.value)} className={styles.select}>
                                      <option value="libre">Libre</option>
                                      <option value="qcm">QCM</option>
                                  </select>
                              </div>

                              <div className={styles.formControl}>
                                  <label htmlFor="editHelper">Indice (optionnel)</label>
                                  <textarea
                                      id="editHelper"
                                      value={editHelper}
                                      onChange={(e) => setEditHelper(e.target.value)}
                                  />
                              </div>

                              <div className={styles.formControl}>
                                  <label htmlFor="editImgHelper">Indice image (optionnelle)</label>
                                  <ImageEditor
                                      key={localTempImageHelper ? localTempImageHelper.name : currentItem.img_helper || 'empty'}
                                      initialImage={
                                          localTempImageHelper
                                          ? URL.createObjectURL(localTempImageHelper)
                                          : currentItem.img_helper
                                              ? `${path}${currentItem.img_helper}`
                                              : null
                                      }
                                      onLocalChange={(file) => {
                                          setLocalTempImageHelper(file);
                                          setShouldDeleteHelperImage(false);
                                      }}
                                      onDelete={() => {
                                          setLocalTempImageHelper(null);
                                          setShouldDeleteHelperImage(true);
                                      }}
                                  />
                              </div>

                              <div className={styles.buttonContainer}>
                              <button className={styles.cancelButton} type="button" onClick={() => {
                                  resetEditModal();
                                  setIsEditModalOpen(false);
                              }}>Annuler</button>
                                  <button className={styles.saveButton} type="submit">Mettre à jour</button>
                              </div>
                          </form>
                      </>
                  )}

                  {activeEditTab === 'B' && (
                      <>
                          <h3>Ajouter des options</h3>

                          <hr className={styles.hr} />

                          <form onSubmit={handleOptionsSubmit} >
                              
                              <div className={styles.formControl}>
                                  <label htmlFor="editKey">Indice clé (optionnel)</label>
                                  <input
                                      type="text"
                                      id="editKey"
                                      value={editKey}
                                      onChange={(e) => setEditKey(e.target.value)}
                                  />
                              </div>

                              <div className={styles.formControl}>
                                  <label htmlFor="editImgKey">Indice clé image (optionnel)</label>
                                  <ImageEditor
                                      key={localTempImageKey ? localTempImageKey.name : currentItem.img_indice || 'empty'}
                                      initialImage={
                                          localTempImageKey
                                          ? URL.createObjectURL(localTempImageKey)
                                          : currentItem.img_indice
                                              ? `${path}${currentItem.img_indice}`
                                              : null
                                      }
                                      onLocalChange={(file) => {
                                          setLocalTempImageKey(file);
                                          setShouldDeleteKeyImage(false);
                                      }}
                                      onDelete={() => {
                                          setLocalTempImageKey(null);
                                          setShouldDeleteKeyImage(true);
                                      }}
                                  />
                              </div>

                              <div className={styles.formControl}>
                                  <label htmlFor="editMessage">Message d'enchaînement (optionnel)</label>
                                  <textarea
                                      id="editMessage"
                                      value={editMessage}
                                      onChange={(e) => setEditMessage(e.target.value)}
                                  />
                              </div>

                              <div className={styles.buttonContainer}>
                              <button className={styles.cancelButton} type="button" onClick={() => {
                                  resetEditModal();
                                  setIsEditModalOpen(false);
                              }}>Annuler</button>
                                  <button className={styles.saveButton} type="submit">Mettre à jour</button>
                              </div>
                          </form>
                      </>
                  )}
              </div>
          </div>
      )}

      {/* ====== Modale de suppression de question ====== */}
      {isDeleteModalOpen && currentItem && (
          <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                  <h3>Supprimer la question</h3>
                  <p>Êtes-vous sûr de vouloir supprimer la question suivante ?</p>
                  <p><strong>{currentItem.libelle}</strong></p>
                  <div className={styles.modalActions}>
                      <button
                          onClick={() => {
                              onDeleteQuestion(currentItem.id);
                              setIsDeleteModalOpen(false);
                          }}
                          className={styles.deleteButton}
                      >
                          Supprimer
                      </button>
                      <button onClick={() => setIsDeleteModalOpen(false)}>Annuler</button>
                  </div>
              </div>
          </div>
      )}

      {/* ====== Modale d'édition de l'image de la question ====== */}
      {currentItem && isEditImageModalOpen && (
      <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
          <h3>Modifier l'image principale</h3>
          <ImageEditor
              key={tempMainImage ? tempMainImage.name : currentItem.img || 'empty'}
              initialImage={
                  tempMainImage ? URL.createObjectURL(tempMainImage) : currentItem.img ? `${path}${currentItem.img}` : null
              }
              onLocalChange={(file) => {
                  setTempMainImage(file);
                  setShouldDeleteMainImage(false);
              }}
              onDelete={() => {
                  setTempMainImage(null);
                  setShouldDeleteMainImage(true);
              }}
          />
          <div className={styles.buttonContainer}>
              <button
              className={styles.cancelButton}
              onClick={() => {
                  setIsEditImageModalOpen(false);
                  setCurrentItem(null);
              }}
              >
              Annuler
              </button>
              <button
              className={styles.saveButton}
              onClick={async () => {
                  await onUpdateQuestionImage(currentItem.id, { img: tempMainImage, delete_img: shouldDeleteMainImage, });
                  setIsEditImageModalOpen(false);
                  setCurrentItem(null);
              }}
              >
              Mettre à jour
              </button>
          </div>
          </div>
      </div>
      )}



      {/* Intégration du composant ReponsesTab pour gérer les réponses */}
      {/* {reponsesModalOpen && selectedQuestion && (
          <ReponsesTab 
              question={selectedQuestion}
              onClose={handleCloseReponsesModal}
          />
      )} */}
    </div>
  );
}

export default QuestionsIndeTab;
